<template>
  <div>
    <!-- Admin Authentication Modal -->
    <div v-if="showAdminAuth" class="auth-modal">
      <AdminAuth @authenticated="handleAdminAuth" />
    </div>

    <!-- Main Content -->
    <div v-else>
      <h2>Tuesday Night at <a href="https://thejunctionharrow.co.uk">The Junction (Harrow)</a> (8-10pm)</h2>
      <form @submit.prevent="handleApply">
        <ul>
          <li v-for="event in events" :key="event.id">
            <label>
              <input type="checkbox" :value="event.id" v-model="selectedEvents" />
              {{ event.event_name }} - {{ formatDateSuffix(event.event_date) }}
            </label>
          </li>
        </ul>
        <div class="button-container">
          <button type="submit" :disabled="selectedEvents.length === 0">Apply for Selected Gigs</button>
        </div>
      </form>

      <!-- Application Form Modal -->
      <div v-if="showForm">
        <ApplicationForm :eventIds="selectedEvents" @close="handleFormClose" />
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from '../supabaseClient';
import ApplicationForm from './ApplicationForm.vue';
import AdminAuth from './AdminAuth.vue';

function getOrdinalSuffix(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

export default {
  name: 'EventList',
  components: {
    ApplicationForm,
    AdminAuth,
  },
  data() {
    return {
      events: [],
      selectedEvents: [],
      showForm: false,
      errorMessage: '',
      showAdminAuth: false,
      pendingRedirect: null,
    };
  },
  created() {
    // Check if admin authentication is required
    if (this.$route.query.auth === 'required') {
      this.showAdminAuth = true;
      this.pendingRedirect = this.$route.query.redirect;
    } else {
      this.fetchData();
    }
  },
  methods: {
    async fetchData() {
      try {
        // Single RPC call to get all data
        const { data, error } = await supabase.rpc('get_event_data');

        if (error) {
          throw error;
        }

        const blackoutSet = new Set(data.blackout_dates?.map(bd => bd.date) || []);
        const existingEvents = data.events;
        const configData = data.config;

        const horizonMonths = configData?.horizon_months || 6;

        // Generate all possible Tuesday dates for next 6 months
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + horizonMonths);

        let currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        const generatedEvents = [];
        const existingEventNames = new Set(existingEvents?.map(event => event.event_name) || []);

        while (currentDate <= endDate) {
          if (currentDate.getDay() === 2) { // Tuesday
            // Use local time for date string to match getDay() day-of-week check
            const year = currentDate.getFullYear();
            const month = currentDate.toLocaleString('en-US', { month: 'short' });
            const day = currentDate.getDate();
            const dateStr = `${year}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const suffix = getOrdinalSuffix(day);
            const formattedDate = `${month} ${day}${suffix} ${year}`;

            // Only include dates that aren't blackout dates and don't have matching event names
            if (!blackoutSet.has(dateStr) && !existingEventNames.has(formattedDate)) {
              generatedEvents.push({
                id: `generated_${dateStr}`,
                event_date: dateStr,
                event_name: formattedDate,
                is_generated: true
              });
            }
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }

        // Combine and sort existing and generated events
        this.events = [...(existingEvents || []), ...generatedEvents]
          .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

      } catch (err) {
        console.error('Unexpected error:', err);
        this.errorMessage = 'An unexpected error occurred while fetching events.';
      }
    },

    handleAdminAuth() {
      this.showAdminAuth = false;
      if (this.pendingRedirect) {
        this.$router.push(this.pendingRedirect);
      } else {
        this.fetchData();
      }
    },

    formatDateSuffix(dateStr) {
      return dateStr.split('T')[0].split('-').reverse().join('/');
    },
    async handleApply() {
      // First, create actual events for any generated ones that were selected
      const selectedGenerated = this.selectedEvents
        .filter(id => typeof id === 'string' && id.startsWith('generated_'))
        .map(id => {
          const dateStr = id.replace('generated_', '');
          const event = this.events.find(e => e.id === id);
          return {
            event_date: dateStr,
            event_name: event.event_name
          };
        });

      if (selectedGenerated.length > 0) {
        // First, check for any existing events with the same date AND name
        const { data: existingEvents, error: checkError } = await supabase
          .from('events')
          .select('id, event_date, event_name')
          .or(selectedGenerated.map(event =>
            `and(event_date.eq.${event.event_date},event_name.eq.${event.event_name})`
          ).join(','));

        if (checkError) {
          console.error('Error checking for existing events:', checkError);
          return;
        }

        // Filter out any events that already exist
        const eventsToCreate = selectedGenerated.filter(newEvent =>
          !existingEvents?.some(existingEvent =>
            existingEvent.event_date === newEvent.event_date &&
            existingEvent.event_name === newEvent.event_name
          )
        );

        // Create new events using upsert to prevent race condition duplicates
        for (const event of eventsToCreate) {
          const { error } = await supabase
            .from('events')
            .upsert(event, { 
              onConflict: 'event_date,venue_id', 
              ignoreDuplicates: true 
            });

          if (error) {
            console.error('Error creating event:', error);
            // If this specific event failed, continue with the others
            continue;
          }
        }
      }

      // Get all selected events that weren't generated (they already exist)
      const realEventIds = this.selectedEvents
        .filter(id => typeof id !== 'string' || !id.startsWith('generated_'));

      // Get IDs for the newly created events
      const { data: newEvents } = await supabase
        .from('events')
        .select('id')
        .in('event_date', selectedGenerated.map(e => e.event_date))
        .in('event_name', selectedGenerated.map(e => e.event_name));

      if (newEvents) {
        realEventIds.push(...newEvents.map(e => e.id));
      }

      // Use the real event IDs when showing the application form
      this.selectedEvents = [...new Set(realEventIds)]; // Ensure no duplicates
      this.showForm = true;
    },
    handleFormClose() {
      this.showForm = false;
      // Do NOT clear selectedEvents, so checkboxes remain selected
    }
  },
};
</script>

<style scoped>
/* Styles scoped for this component */
ul {
  list-style: none; /* Remove default bullet points */
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center; /* Center the entire list on the page */
  margin-bottom: 20px; /* Add some space before the button */
}

ul li {
  width: 300px; /* Consistent width for each item */
  display: flex;
  align-items: center;
  justify-content: flex-start; /* Left-align the checkbox and label */
  padding: 10px 0;
  padding-left: 60px;
}

ul li input[type="checkbox"] {
  margin-right: 10px; /* Space between checkbox and label */
}

.button-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 20px;
}

button[type="submit"] {
  padding: 10px 20px;
  font-size: 16px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}

button[type="submit"]:hover {
  background-color: #2980b9;
}

.generated-date {
  background-color: #f8f9fa;
  border-radius: 4px;
}

.generated-tag {
  font-size: 0.8em;
  color: #666;
  margin-left: 8px;
  font-style: italic;
}
.auth-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
</style>
